import React, { useState, useEffect } from 'react';

import { getCrewNotifications } from '@go-client/lib/api/crewSchedule';
import { BareStaffNotification } from '@go-shared/types/staffNotification';
import {
  timeToString,
  timeToAgoLocal,
  timeToMinutes,
  minutesToTimeString,
  DEFAULT_HOUR_FORMAT,
  DEFAULT_TIMESTR_FORMAT,
} from '@go-shared/lib/time';

import './CrewNotifications.scss';
import { InlineIcon } from '@spark-labs/avocado-ui';

interface CrewNotificationsProps {
  staffRoleId: string;
}

export const CrewNotifications = ({ staffRoleId }: CrewNotificationsProps) => {
  const [isOpen, setisOpen] = useState(false);
  const [notifications, setNotifications] = useState<BareStaffNotification[]>([]);
  const [status, setStatus] = useState<'loading' | 'loaded'>('loaded');
  useEffect(() => {
    getNotifications().then().catch(console.error);
  }, []);

  const getNotifications = async () => {
    setNotifications((await getCrewNotifications(staffRoleId)).reverse());
  };
  return (
    <>
      <button className="ml-auto mr-8 relative" onClick={() => setisOpen(!isOpen)}>
        <InlineIcon className="w-5 h-5 text-white" name="bell" />
        {notifications.length !== 0 && (
          <div className="notifications-blurb bg-yellow-lighter">
            <div>{notifications.length}</div>
          </div>
        )}
      </button>
      <div className={`notifications-wrapper ${isOpen ? 'open' : ''}`}>
        <div
          className="inline-flex px-3 py-3 mt-2 mb-1 justify-between
                        w-full notifications-header"
        >
          <h3>Notifications</h3>
          <button
            disabled={status === 'loading'}
            className="refresh-notifications mr-auto ml-3"
            onClick={async () => {
              setStatus('loading');
              setTimeout(() => {
                setStatus('loaded');
              }, 300);
              await getNotifications();
            }}
          >
            {status === 'loading' ? 'Refreshing' : 'Refresh'}
          </button>
          <button onClick={() => setisOpen(!isOpen)}>
            <h3 className="text-blue-light text-xs">Close</h3>
          </button>
        </div>
        <div className={notifications.length !== 0 ? 'overflow-y-auto' : 'h-full'}>
          {notifications.length !== 0 ? (
            <ul>
              {notifications.map((notification, i) => {
                return (
                  <li className="px-3" key={i}>
                    <NotificationItem {...notification} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyNotifications />
          )}
        </div>
      </div>
    </>
  );
};

const EmptyNotifications = () => {
  return (
    <div className="w-3/4 flex justify-center items-center mx-auto empty-notifications">
      <h2 className="text-blue-light text-center text-xl">
        There are no notifications at the moment
      </h2>
    </div>
  );
};

const NotificationItem = (props: BareStaffNotification) => {
  const { type, value } = getNotficationType(props);
  return (
    <div
      className="notification-item
                py-4 rounded inline-flex justify-between w-full items-center"
    >
      <span className={`notification-type rounded-full py-2 w-1/4 text-center  ${type}`}>
        {value}
      </span>
      <div className="items-center flex flex-col justify-start mr-auto ml-3">
        <h3 className="text-xs mb-1 mr-auto">{getNotificationTitle(props)}</h3>
        <span className="formatted-time-change inline-flex mr-auto">
          {props.updatedValue && (
            <>
              <p className="text-xs mr-2">
                {timeToString(
                  props.updatedValue.startTime,
                  DEFAULT_HOUR_FORMAT,
                  DEFAULT_TIMESTR_FORMAT
                )}
              </p>
              <span className="mr-2 text-xs">
                <strong>changed to</strong>
              </span>
            </>
          )}
          {(props.updatedValue && props.previousValue) || props.updatedValue ? (
            <p className="text-xs">
              {minutesToTimeString(
                timeToMinutes(props.updatedValue.startTime) + props.updatedValue.duration,
                DEFAULT_HOUR_FORMAT,
                DEFAULT_TIMESTR_FORMAT
              )}
            </p>
          ) : null}
        </span>
      </div>
      <span className="notification-ago text-right w-1/5">
        {timeToAgoLocal(new Date(props.modificationDate), new Date())}
      </span>
    </div>
  );
};

const getNotificationTitle = (notification: BareStaffNotification) => {
  if (notification.updatedValue) {
    return (
      <>
        <span className="mr-1">{notification.updatedValue.title}</span>@
        <span className="ml-1">{notification.updatedValue.venue.name} </span>
      </>
    );
  } else if (notification.previousValue) {
    return (
      <>
        <span className="mr-1">{notification.previousValue.title}</span>@
        <span className="ml-1">{notification.previousValue.venue.name} </span>
      </>
    );
  }
};
const getNotficationType = (notification: BareStaffNotification) => {
  const prev = notification.previousValue;
  const updated = notification.updatedValue;
  if (!prev && updated) return { type: 'created', value: 'Assigned' };
  else if (prev && !updated) return { type: 'cancelled', value: 'Cancelled' };
  else if (prev && updated) return { type: 'changed', value: 'Modified' };
  else return { type: '', value: '' };
};
